import React, { useEffect, useRef, useState } from "react";

import { get, isEmpty, find, map, includes, size } from "lodash";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

import { ROUTES } from "./constants/routes.constants";
import { getRefreshToken } from "./helpers/generel.helpers";
import AppSkeleton from "./components/appSkeleton/AppSkeleton";
import Loader from "./components/loader/Loader";
import Login from "./pages/login";
import Register from "./pages/register";
import PurchaseVerify from "./pages/purchaseVerify";
import SelectPlan from "./pages/selectPlan";
import PersonalInfo from "./pages/personalInfo";
import BusinessInfo from "./pages/businessInfo";
import ForgotPassword from "./pages/forgotPassword";
import Resetpassword from "./pages/resetpassword";
import EmailVerify from "./pages/emailVerify";
import Homepage from "./pages/homepage";
import Schedule from "./pages/schedule";
import MyPlaylistPage from "./pages/myPlaylistPage";
import EditPlaylistPage from "./pages/editPlaylistPage";
import LikedPage from "./pages/likedPage";
import ArtistPage from "./pages/artistPage";
import ArtistDetailPage from "./pages/artistDetailPage";
import playlistStore from "./zustand/playlist.store";
import userStore from "./zustand/user.store";
import musicPlayerStore from "./zustand/musicPlayer.store";
import likedSongsStore from "./zustand/likedSong.store";
import SchedulesStore from "./zustand/Schedule.store";
import ignoredSongStore from "./zustand/ignoreSong.store";
import favoriteSongStore from "./zustand/favoriteSong.store";
import disLikedSongsStore from "./zustand/dislikedSong.store";
import { appActions, userActions } from "./actions";
import { billingAccessHelpers, generalHelpers, indexDbHelpers } from "./helpers";
import Page from "./components/profilePage";
import {
  getAuthFromIndexDB,
  getUserFromIndexDB,
  storeAuthInIndexDB,
  storeUserInIndexDB,
} from "./helpers/indexDb.helpers";
import { routesConstants } from "./constants";
import PopUp from "./components/popUp";
import { ICONS } from "./constants/icons.constants";
import Payment from "./pages/payment/Payment";
import PaymentProcessing from "./pages/paymentProcessing/PaymentProcessing";

import "./App.css";
import ChangePlan from "./pages/changePlan/ChangePlan";

const findFirstUnIngoredSong = (songs, ignoredSongs) => {
  const ingoreSongIds = map(ignoredSongs, (i) => i.id);
  let index = 0;
  for (let i = 0; i < size(songs); i++) {
    if (!includes(ingoreSongIds, songs[i].id)) {
      index = i;
      break;
    }
  }
  return index;
};

const App = () => {
  const currentRoute = useLocation();
  const navigate = useNavigate();
  const [billingStatus, setBillingStatus] = useState(null);
  const [accessLevel, setAccessLevel] = useState(null);

  const {
    setUser,
    user,
    setAuthentication,
    authenticate,
    userOffline,
    setUserOffline,
    defaultUser,
  } = userStore((state) => ({
    setUser: state.setUser,
    user: state.user,
    defaultUser: state.defaultUser,
    setAuthentication: state.setAuthentication,
    authenticate: state.authenticate,
    userOffline: state.userOffline,
    setUserOffline: state.setUserOffline,
  }));
  const { setPlaylists } = playlistStore((state) => ({
    setPlaylists: state.setPlaylists,
  }));
  const { setSchedules } = SchedulesStore((state) => ({
    setSchedules: state.setSchedules,
  }));
  const { songs } = musicPlayerStore((state) => {
    return {
      songs: state.songs,
      setBeforeShuffleSongs: state.setBeforeShuffleSongs,
    };
  });
  const { setSongs: setLikedSongs } = likedSongsStore((state) => {
    return {
      setSongs: state.setSongs,
    };
  });
  const { setSongs: setDislikeSongs } = disLikedSongsStore((state) => {
    return {
      setSongs: state.setSongs,
    };
  });
  const { setSongs: setIgnoredSongs } = ignoredSongStore((state) => {
    return {
      setSongs: state.setSongs,
    };
  });
  const { setSongs: setFavouriteSongs } = favoriteSongStore((state) => {
    return {
      setSongs: state.setSongs,
    };
  });
  const {
    isPlaying,
    currentAudioRefIndex,
    audioRef2,
    aoudioRef1,
    setPlayList,
    toggleIsShuffle,
    playlist,
  } = musicPlayerStore((state) => {
    return {
      isPlaying: state.isPlaying,
      currentAudioRefIndex: state.currentAudioRefIndex,
      audioRef2: state.audioRef2,
      aoudioRef1: state.aoudioRef1,
      setPlayList: state.setPlayList,
      toggleIsShuffle: state.toggleIsShuffle,
      playlist: state.playList,
    };
  });

  const [isLoading, setIsLoading] = useState(true);
  const [visible, setVisible] = useState(false);

  const canvasRef = useRef(null);
  const toCallOneTime = useRef(false);

  const refreshToken = getRefreshToken(userOffline);
  const isAuthenticated = !isEmpty(user?.id || "");

  // Aoudio Visualizer
  const audioContext1 = useRef(null);
  const audioContext2 = useRef(null);
  const source1 = useRef(null);
  const source2 = useRef(null);
  const analyzer1 = useRef(null);
  const analyzer2 = useRef(null);
  const getCurrentAudioRef = () =>
    currentAudioRefIndex?.current === 1 ? aoudioRef1 : audioRef2;
  const getCurrentSourceRef = () =>
    currentAudioRefIndex?.current === 1 ? source1 : source2;
  const getCurrentAnalyzerRef = () =>
    currentAudioRefIndex?.current === 1 ? analyzer1 : analyzer2;

  //! Don't ever touch this useEffect
  useEffect(() => {
    const visualizeData = () => {
      const currentSongRef = getCurrentAudioRef();
      const analyzer = getCurrentAnalyzerRef();

      if (
        !currentSongRef ||
        currentSongRef.paused ||
        !analyzer.current ||
        !canvasRef.current
      ) {
        cancelAnimationFrame(visualizeData);
      }
      try {
        const songData = new Uint8Array(100);
        analyzer.current.getByteFrequencyData(songData);
        const bar_width = 4;
        let start = 0;
        const ctx = canvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        for (let i = 0; i < songData.length; i++) {
          // compute x coordinate where we would draw
          start = i * 7;
          //create a gradient for the  whole canvas
          let gradient = ctx.createLinearGradient(
            0,
            0,
            canvasRef.current.width,
            canvasRef.current.height
          );
          // gradient.addColorStop(0.2, "#9438fb");
          // gradient.addColorStop(0.5, "#21e4ef");
          // gradient.addColorStop(0.75, "#75df51");
          // gradient.addColorStop(1.0, "#292cf9");

          gradient.addColorStop(0.2, "#9438fb");
          gradient.addColorStop(0.5, "#9438fb");
          gradient.addColorStop(0.75, "#9438fb");
          gradient.addColorStop(1.0, "#9438fb");
          ctx.fillStyle = gradient;
          ctx.fillRect(
            start,
            canvasRef.current.height * 1.6,
            bar_width,
            -songData[i]
          );
        }

        requestAnimationFrame(visualizeData);
      } catch (err) {
        return;
      }
    };

    if (!isAuthenticated) return;
    // if(!isPlaying) return;

    if (isPlaying) {
      if (!audioContext1.current) audioContext1.current = new AudioContext();
      if (!audioContext2.current) audioContext2.current = new AudioContext();
    }

    try {
      source1.current =
        audioContext1.current.createMediaElementSource(aoudioRef1);
      analyzer1.current = audioContext1.current.createAnalyser();
      source1.current.connect(analyzer1.current);
      analyzer1.current.connect(audioContext1.current.destination);
    } catch (e) {}
    try {
      source2.current =
        audioContext2.current.createMediaElementSource(audioRef2);
      analyzer2.current = audioContext2.current.createAnalyser();
      source2.current.connect(analyzer2.current);
      analyzer2.current.connect(audioContext2.current.destination);
    } catch (e) {}
    //
    if (isPlaying) {
      audioContext1.current?.resume();
      audioContext2.current?.resume();
    } else {
      audioContext1.current?.suspend();
      audioContext2.current?.suspend();
    }

    //

    visualizeData();

    return () => {
      cancelAnimationFrame(visualizeData);
    };
  }, [isAuthenticated, isPlaying, songs, currentRoute.pathname]);

  useEffect(() => {
    (async () => {
      if (navigator.onLine) {
        setUserOffline(false);
        console.log("loaddd  online");
      } else {
        setUserOffline(true);
        const userfromDb = await getUserFromIndexDB("user-data");
        const authData = await getAuthFromIndexDB("authentication-data");
        setUser(userfromDb.user);
        setAuthentication(authData.auth);
      }
    })();
  }, []);

  useEffect(() => {
    // window.addEventListener("load", async () => {
    //   console.log("in before unload", navigator.onLine);

    //   if (navigator.onLine) {
    //     setUserOffline(false);
    //     console.log("loaddd online");
    //   } else {
    //     setUserOffline(true);
    //     const userfromDb = await getUserFromIndexDB("user-data");
    //     const authData = await getAuthFromIndexDB("authentication-data");
    //     setUser(userfromDb.user);
    //     setAuthentication(authData.auth);
    //   }
    // });

    window.addEventListener("online", () => {
      setUserOffline(false);
      console.log("online user");
    });

    window.addEventListener("offline", async () => {
      setUserOffline(true);
      console.log("offline userrr");
      toCallOneTime.current = false;

      const userfromDb = await getUserFromIndexDB("user-data");
      const authData = await getAuthFromIndexDB("authentication-data");
      setUser(userfromDb.user);
      setAuthentication(authData.auth);
    });

    // remove ebvent listener
    return () => {
      window.removeEventListener("resize", () => {});
      window.removeEventListener("online", () => {});
      window.removeEventListener("offline", () => {});
    };
  }, [user]);

  useEffect(() => {
    (async () => {
      if (userOffline) {
        const userfromDb = await getUserFromIndexDB("user-data");
        const authData = await getAuthFromIndexDB("authentication-data");
        const offlinePlaylist = await indexDbHelpers.getPlaylistsFromDB();

        const favouritePlayList = find(offlinePlaylist, (i) => i.is_favourite);
        const initialPlaylit = favouritePlayList
          ? favouritePlayList
          : offlinePlaylist?.[0];

        setPlaylists(offlinePlaylist);
        setUser(userfromDb.user);
        setAuthentication(authData.auth);
        setPlayList(initialPlaylit, 0);
        setBillingStatus(null);
        setAccessLevel(null);
        setIsLoading(false);
        return;
      } else {
        if (toCallOneTime.current) return;

        toCallOneTime.current = true;

        const refreshTokenData = await userActions.refreshTokenAction(
          userOffline
        );

        if (!refreshTokenData.success) {
          setIsLoading(false);
          return;
        }

        const user_id = get(refreshTokenData, "id", "");
        const data = await userActions.fetchUserAction(user_id, userOffline);
        const fetchUserStatus = get(data, "status", null);
        const code = get(data, "data.code", "");
        
        // If backend returns 304 Not Modified, axios returns empty body.
        // In that case, use cached user/auth from IndexedDB to avoid logging out.
        if (fetchUserStatus === 304) {
          const userfromDb = await getUserFromIndexDB("user-data");
          const authData = await getAuthFromIndexDB("authentication-data");
          if (userfromDb?.user) setUser(userfromDb.user);
          if (authData?.auth) setAuthentication(authData.auth);

          const cachedSubscriptionStatus = get(userfromDb, "user.subscription_status", null);
          if (cachedSubscriptionStatus) {
            setAccessLevel(
              billingAccessHelpers.getAccessLevelFromSubscriptionStatus(
                cachedSubscriptionStatus
              )
            );
          }

          // Try to refresh billing status in background (non-blocking)
          try {
            const billingRes = await userActions.fetchBillingStatusAction(userOffline);
            if (billingRes?.success) {
              const status = get(billingRes, "data", null);
              setBillingStatus(status);
              setAccessLevel(
                billingAccessHelpers.getAccessLevelFromBillingStatus(status)
              );
            }
          } catch (e) {
            // ignore
          }

          setIsLoading(false);
          return;
        }

        // Check for new response format (has onboarding_step, can_access_player)
        // Try multiple possible response structures for robustness
        let responseData = get(data, "data.data", null);
        if (!responseData || !get(responseData, "user")) {
          responseData = get(data, "data", {});
        }
        const onboardingStep = get(responseData, "onboarding_step", undefined);
        const canAccessPlayer = get(responseData, "can_access_player", undefined);
        const hasActiveSubscription = get(responseData, "has_active_subscription", undefined);
        const userFromResponse = get(responseData, "user", null);
        
        // Also check if subscription info is embedded in user object (alternative format)
        const subscriptionStatusFromUser = get(userFromResponse, "subscription_status", null);

        const refreshBillingStatus = async () => {
          const billingRes = await userActions.fetchBillingStatusAction(userOffline);
          if (billingRes?.success) {
            const status = get(billingRes, "data", null);
            setBillingStatus(status);
            setAccessLevel(
              billingAccessHelpers.getAccessLevelFromBillingStatus(status)
            );
          } else {
            setBillingStatus(null);
            setAccessLevel(null);
          }
        };

        // New response format: use onboarding_step and can_access_player
        // Detect new format by presence of user object with subscription_status OR explicit flags
        const isNewResponseFormat = userFromResponse && (
          onboardingStep !== undefined || 
          canAccessPlayer !== undefined ||
          hasActiveSubscription !== undefined ||
          subscriptionStatusFromUser
        );
        
        if (isNewResponseFormat) {
          // Check if user is blocked
          if (get(userFromResponse, "status", "") === "BLOCKED") {
            setAuthentication({
              isAuthenticated: true,
              isPhoneVerified: true,
              isBusinessVerified: true,
              isPrimium: true,
            });
            setVisible(true);
            generalHelpers.toaster("error", "You are Blocked by admin");
            setIsLoading(false);
            return;
          }

          // Get auth state from helper
          const authState = billingAccessHelpers.getAuthStateFromResponse(responseData);
          
          // If can access player, load full app data
          if (canAccessPlayer) {
            const {
              user: freshUser,
              playlists,
              status,
              likedSongs,
              ignoredSongs,
              favouriteSongs,
              dislikedSongs,
              schedules,
              favoritePlaylist,
            } = await appActions.fetchAppData(user_id, userOffline);

            const indexToStore = findFirstUnIngoredSong(
              favoritePlaylist?.songs,
              ignoredSongs
            );

            await storeUserInIndexDB(freshUser, "user-data");
            setUser(freshUser);
            setPlaylists(playlists);
            setFavouriteSongs(favouriteSongs);
            setLikedSongs(likedSongs);
            setDislikeSongs(dislikedSongs);
            setIgnoredSongs(ignoredSongs);
            setSchedules(schedules);
            setPlayList(favoritePlaylist, indexToStore);
          } else {
            // Just set the user from response
            setUser(userFromResponse);
          }

          const authData = {
            isAuthenticated: authState.isAuthenticated,
            isPhoneVerified: authState.isPhoneVerified,
            isBusinessVerified: authState.isBusinessVerified,
            isPrimium: authState.isPrimium,
          };
          storeAuthInIndexDB(authData, "authentication-data");
          setAuthentication(authData);

          // Set access level directly from subscription status (don't wait for billing API)
          const directAccessLevel = billingAccessHelpers.getAccessLevelFromSubscriptionStatus(
            subscriptionStatusFromUser
          );
          setAccessLevel(directAccessLevel);
          
          // Also try to refresh billing status for full billing info (non-blocking)
          refreshBillingStatus().catch(() => {
            // Ignore errors - we already have access level from user data
          });

          setIsLoading(false);
          return;
        }

        // Fallback: Old response format (backward compatibility)
        if (code === "PHONE_NOT_VERIFIED") {
          setAuthentication({
            isAuthenticated: true,
            isPhoneVerified: false,
            isBusinessVerified: false,
            isPrimium: false,
          });
          setUser({
            ...defaultUser,
            id: user_id,
          });
          setBillingStatus(null);
          setAccessLevel(null);
          navigate(routesConstants.ROUTES.PERSONAL_INFO);
        } else if (code === "BUSINESS_NOT_FOUND") {
          setAuthentication({
            isAuthenticated: true,
            isPhoneVerified: true,
            isBusinessVerified: false,
            isPrimium: false,
          });
          setUser({
            ...defaultUser,
            id: user_id,
          });
          setBillingStatus(null);
          setAccessLevel(null);
        } else if (code === "PRIMIUM_NOT_FOUND") {
          setAuthentication({
            isAuthenticated: true,
            isPhoneVerified: true,
            isBusinessVerified: true,
            isPrimium: false,
          });
          setUser({
            ...defaultUser,
            id: user_id,
          });
          await refreshBillingStatus();
        } else if (code === "USER_BLOCKED") {
          setAuthentication({
            isAuthenticated: true,
            isPhoneVerified: true,
            isBusinessVerified: true,
            isPrimium: true,
          });

          setVisible(true);

          generalHelpers.toaster("error", "You are Blocked by admin");
          setIsLoading(false);
          return;
        } else if (code === "OK") {
          setUser(get(data, "data", {}));

          const authData = {
            isAuthenticated: true,
            isPhoneVerified: true,
            isBusinessVerified: true,
            isPrimium: true,
          };
          storeAuthInIndexDB(authData, "authentication-data");

          const {
            user,
            playlists,
            status,
            likedSongs,
            ignoredSongs,
            favouriteSongs,
            dislikedSongs,
            schedules,
            favoritePlaylist,
          } = await appActions.fetchAppData(user_id, userOffline);

          // const index = findIndex(
          //   favoritePlaylist.songs,
          //   (song) => get(song, "id", "") === status?.last_played_song
          // );
          // const indexToStore = index === -1 ? 0 : index;
          const indexToStore = findFirstUnIngoredSong(
            favoritePlaylist?.songs,
            ignoredSongs
          );

          await storeUserInIndexDB(user, "user-data");
          setUser(user);
          setPlaylists(playlists);
          setFavouriteSongs(favouriteSongs);
          setLikedSongs(likedSongs);
          setDislikeSongs(dislikedSongs);
          setIgnoredSongs(ignoredSongs);
          setSchedules(schedules);
          setPlayList(favoritePlaylist, indexToStore);
          // toggleIsShuffle(status?.last_shuffled);

          setAuthentication({
            isAuthenticated: true,
            isPhoneVerified: true,
            isBusinessVerified: true,
            isPrimium: true,
          });

          // Keep billing-status cached for UX + future access control decisions.
          await refreshBillingStatus();

          setIsLoading(false);
        } else {
          setAuthentication({
            isAuthenticated: false,
            isPhoneVerified: false,
            isBusinessVerified: false,
            isPrimium: false,
          });
          setBillingStatus(null);
          setAccessLevel(null);
          // setUser(get(data, "data", {}));
        }
        setIsLoading(false);
      }
    })();
  }, [userOffline]);

  // Route guard for non-FULL access levels (PAST_DUE/PAUSED/NONE/CANCELED).
  useEffect(() => {
    if (!authenticate.isAuthenticated || !authenticate.isBusinessVerified) return;

    // If user is premium in current auth state, allow everything.
    const resolvedAccessLevel =
      authenticate.isPrimium
        ? billingAccessHelpers.ACCESS_LEVELS.FULL
        : accessLevel || billingAccessHelpers.ACCESS_LEVELS.BILLING_ONLY;

    if (resolvedAccessLevel === billingAccessHelpers.ACCESS_LEVELS.FULL) return;

    const pathname = currentRoute.pathname;
    const search = currentRoute.search || "";
    const returnTo = `${pathname}${search}`;

    // Special-case: Payment route must have a transactionId (and usually paymentUrl).
    if (billingAccessHelpers.isPaymentRoute(pathname)) {
      const params = new URLSearchParams(search);
      const txId = params.get("transactionId");
      const paymentUrl = params.get("paymentUrl");
      if (!txId || !paymentUrl) {
        navigate(ROUTES.PACKAGES, { replace: true, state: { returnTo } });
        return;
      }
      return;
    }

    if (billingAccessHelpers.isPlayerRoute(pathname)) {
      const redirectTo = billingAccessHelpers.getRedirectForBlockedRoute({
        accessLevel: resolvedAccessLevel,
        billingStatus,
      });

      if (redirectTo && redirectTo !== pathname) {
        // Preserve user's intent so after fixing billing they can return.
        try {
          sessionStorage.setItem("billing:returnTo", returnTo);
        } catch (e) {
          // ignore
        }
        const status = get(billingStatus, "subscription_status", "");
        if (status === billingAccessHelpers.SUBSCRIPTION_STATUS.PAST_DUE) {
          generalHelpers.toaster(
            "error",
            "Payment failed. Please update your payment method to continue."
          );
        } else if (status === billingAccessHelpers.SUBSCRIPTION_STATUS.PAUSED) {
          generalHelpers.toaster(
            "info",
            "Please complete checkout to continue your subscription."
          );
        } else {
          generalHelpers.toaster("info", "Please subscribe to continue.");
        }
        const openTab =
          redirectTo === ROUTES.PROFILE &&
          status === billingAccessHelpers.SUBSCRIPTION_STATUS.PAST_DUE
            ? "plans"
            : undefined;
        navigate(redirectTo, { replace: true, state: { returnTo, openTab } });
      }
    }
  }, [
    authenticate.isAuthenticated,
    authenticate.isBusinessVerified,
    authenticate.isPrimium,
    accessLevel,
    billingStatus,
    currentRoute.pathname,
    currentRoute.search,
    navigate,
  ]);

  // useEffect(() => {

  // }, []);

  if (isLoading) {
    return <Loader isLoading={isLoading} />;
  }

  // if (!isAuthenticated && isEmpty(refreshToken)) {
  //   return <Login />;
  // }

  return (
    <>
      {(() => {
        // Determine effective access level from state OR user's subscription_status
        const userSubscriptionStatus = get(user, "subscription_status", null);
        const effectiveAccessLevel = accessLevel || 
          (userSubscriptionStatus ? billingAccessHelpers.getAccessLevelFromSubscriptionStatus(userSubscriptionStatus) : null);
        
        const isFull = authenticate.isPrimium || effectiveAccessLevel === billingAccessHelpers.ACCESS_LEVELS.FULL;
        const isBillingOnly = effectiveAccessLevel === billingAccessHelpers.ACCESS_LEVELS.BILLING_ONLY;
        
        // FULL access users (ACTIVE, TRIAL): Show full player experience
        if (isFull) {
          return (
            <Routes>
              {/* Payment page - full page without sidebar */}
              <Route path={ROUTES.PAYMENT} element={<Payment />} />
              <Route
                path={ROUTES.PAYMENT_PROCESSING}
                element={<PaymentProcessing />}
              />
              
              {/* All other routes with sidebar */}
              <Route path="*" element={
                <AppSkeleton>
                  <Routes>
                    <Route path={ROUTES.HOME} element={<Homepage ref={canvasRef} />} />
                    <Route path={ROUTES.PROFILE} element={<Page />} />
                    <Route path={ROUTES.MY_SCHEDULE} element={<Schedule />} />
                    <Route path={ROUTES.MY_PLAYLIST} element={<MyPlaylistPage />} />
                    <Route
                      path={ROUTES.MY_PLAYLIST_EDIT}
                      element={<EditPlaylistPage />}
                    />
                    <Route path={ROUTES.NEW_PLAYLIST} element={<EditPlaylistPage />} />
                    <Route path={ROUTES.LIKED} element={<LikedPage />} />
                    <Route path={ROUTES.ARTIST} element={<ArtistPage />} />
                    <Route path={ROUTES.ARTIST_DETAIL} element={<ArtistDetailPage />} />
                    <Route path={ROUTES.PLANS} element={<ChangePlan />} />
                    <Route path="*" element={<Homepage ref={canvasRef} />} />
                  </Routes>
                </AppSkeleton>
              } />
            </Routes>
          );
        }
        
        // BILLING_ONLY users (PAST_DUE, PAUSED): Show sidebar but hide player
        if (authenticate.isBusinessVerified && isBillingOnly) {
          return (
            <Routes>
              {/* Payment page - full page without sidebar */}
              <Route path={ROUTES.PAYMENT} element={<Payment />} />
              <Route
                path={ROUTES.PAYMENT_PROCESSING}
                element={<PaymentProcessing />}
              />
              
              {/* Other routes with sidebar */}
              <Route path={ROUTES.PROFILE} element={
                <AppSkeleton hidePlayer restrictedMode>
                  <Page />
                </AppSkeleton>
              } />
              <Route path={ROUTES.PLANS} element={
                <AppSkeleton hidePlayer restrictedMode>
                  <ChangePlan />
                </AppSkeleton>
              } />
              <Route path={ROUTES.PACKAGES} element={
                <AppSkeleton hidePlayer restrictedMode>
                  <SelectPlan />
                </AppSkeleton>
              } />
              {/* Redirect all other routes to Profile for billing resolution */}
              <Route path="*" element={
                <AppSkeleton hidePlayer restrictedMode>
                  <Page />
                </AppSkeleton>
              } />
            </Routes>
          );
        }
        
        // NO_ACCESS users (NONE, CANCELED): Show sidebar only on specific routes
        if (authenticate.isBusinessVerified) {
          return (
            <Routes>
              {/* Packages is an "auth route" during onboarding - no sidebar */}
              <Route path={ROUTES.PACKAGES} element={<SelectPlan />} />
              
              {/* Profile and related billing routes show the sidebar (inside the layer) */}
              <Route path={ROUTES.PROFILE} element={
                <AppSkeleton hidePlayer restrictedMode>
                  <Page />
                </AppSkeleton>
              } />
              <Route path={ROUTES.PLANS} element={
                <AppSkeleton hidePlayer restrictedMode>
                  <ChangePlan />
                </AppSkeleton>
              } />
              <Route path={ROUTES.PREMIUM_USER} element={
                <AppSkeleton hidePlayer restrictedMode>
                  <PurchaseVerify />
                </AppSkeleton>
              } />
              <Route path={ROUTES.PAYMENT} element={<Payment />} />
              <Route
                path={ROUTES.PAYMENT_PROCESSING}
                element={<PaymentProcessing />}
              />
              
              {/* Default: redirect to Packages for subscription */}
              <Route path="*" element={<SelectPlan />} />
            </Routes>
          );
        }
        
        // Phone verified but business not verified
        if (authenticate.isPhoneVerified) {
          return (
            <Routes>
              <Route path={ROUTES.BUSINESS_INFO} element={<BusinessInfo />} />
              <Route path="*" element={<BusinessInfo />} />
            </Routes>
          );
        }
        
        // Authenticated but phone not verified
        if (authenticate.isAuthenticated) {
          return (
            <Routes>
              <Route path={ROUTES.PERSONAL_INFO} element={<PersonalInfo />} />
              <Route path="*" element={<PersonalInfo />} />
            </Routes>
          );
        }
        
        // Not authenticated - show login
        return (
          <Routes>
            <Route path={ROUTES.LOGIN} element={<Login />} />
            <Route path={ROUTES.REGISTER} element={<Register />} />
            <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
            <Route path={ROUTES.RESET_PASSWORD} element={<Resetpassword />} />
            <Route path={ROUTES.EMAIL_VERIFIED} element={<EmailVerify />} />
            <Route path="*" element={<Login />} />
          </Routes>
        );
      })()}

      {/* <ToastContainer 
        position="bottom-right"
        autoClose={3000}
        hideProgressBar
        newestOnTop={false}
        draggable={false}
        closeOnClick
        pauseOnHover
        // progress = {undefined}
        theme={"light"}
        style={{ fontSize: "1.4rem" }}
      /> */}

      <PopUp
        isOpen={visible}
        // setIsOpen={setVisible}
        title="You are blocked by admin. Please contact admin for more information."
        iconType={ICONS.ERROR}
        btnPlaceholder="Ok"
        isBtnVisible={false}
        // onSuccess={handleDeleteSchedule}
        // onCancle={() => setVisible(false)}
        // id={playlistId}
        // isLoading={isLoading}
      />
    </>
  );
};

export default App;
